import { combineLatest, type Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import type { Maybe } from '@ardoq/common-helpers';
import {
  NUMBER_OF_ROWS_PER_PAGE,
  type ReportPageAndPagination,
  type ReportPagination,
} from '@ardoq/report-reader';
import { isPptxExportRunning$ } from 'presentation/pptxExport/pptxExport$';
import { readerCaptureMode$ } from 'presentation/pptxExport/readerCaptureMode$';
import type { ReportReader$State } from './types';

export const REPORT_CAPTURE_ROWS = 500;

const CAPTURE_PAGE: ReportPageAndPagination = {
  page: 1,
  pagination: { from: 0, size: REPORT_CAPTURE_ROWS },
};

const FIRST_PAGE: ReportPageAndPagination = {
  page: 1,
  pagination: { from: 0, size: NUMBER_OF_ROWS_PER_PAGE },
};

type SlidePagination = Pick<
  ReportReader$State,
  'page' | 'pagination' | 'paginationBeforeCapture'
>;

export const isReportCaptureMode$: Observable<boolean> = combineLatest([
  isPptxExportRunning$,
  readerCaptureMode$,
]).pipe(
  map(([isExporting, isReaderCapture]) => isExporting || isReaderCapture),
  distinctUntilChanged()
);

export const toCapturePagination = (
  state: ReportReader$State,
  isCaptureMode: boolean
): ReportReader$State => {
  if (isCaptureMode) {
    return {
      ...state,
      ...CAPTURE_PAGE,
      paginationBeforeCapture: state.paginationBeforeCapture ?? {
        page: state.page,
        pagination: state.pagination,
      },
    };
  }
  return state.paginationBeforeCapture
    ? {
        ...state,
        ...state.paginationBeforeCapture,
        paginationBeforeCapture: null,
      }
    : state;
};

export const toSlidePagination = ({
  paginationBeforeCapture,
}: ReportReader$State): SlidePagination =>
  paginationBeforeCapture
    ? { ...CAPTURE_PAGE, paginationBeforeCapture: FIRST_PAGE }
    : { ...FIRST_PAGE, paginationBeforeCapture: null };

type ShownResults = {
  pagination: ReportPagination;
};

const hasCaptureResults = (searchResults: Maybe<ShownResults>): boolean =>
  !searchResults ||
  (searchResults.pagination.from === CAPTURE_PAGE.pagination.from &&
    searchResults.pagination.size === CAPTURE_PAGE.pagination.size);

type ReportCaptureSources = Pick<
  ReportReader$State,
  | 'paginationBeforeCapture'
  | 'isSearchBeingExecuted'
  | 'isAggregatesBeingExecuted'
> & {
  isLoading: boolean;
  searchResults: Maybe<ShownResults>;
};

export const isReportCaptureBusy = ({
  paginationBeforeCapture,
  isLoading,
  isSearchBeingExecuted,
  isAggregatesBeingExecuted,
  searchResults,
}: ReportCaptureSources): boolean =>
  isLoading ||
  isSearchBeingExecuted ||
  isAggregatesBeingExecuted ||
  (paginationBeforeCapture !== null && !hasCaptureResults(searchResults));
